import { useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { appliquerRestitution, joursGardiennage } from "./livrerRestituer"
import {
	fetchDepVehicules,
	fetchDepannage,
	fetchDepannageReset,
	fetchDests,
	fetchEmployes,
	setDep,
} from "../../store/actions/depannageActions"
import {
	fetchMarques,
	fetchMoyenPaiements,
	fetchSecteurs,
	fetchAutreEntites,
} from "../../store/actions/autreActions"
import { fetchPointsInterets } from "../../store/actions/pointInteretActions"
import { useMoyenPaiementFacture } from "../../hooks/moyenPaiement"
import { SECTEURS_POLICE } from "../../utilities/Utils"
import {
	mergeTransactionsV2,
	appliquerPerte,
} from "../../utilities/transactionsV2"
import { PAYE_PAR_MASTER } from "../atelier/AtelierLigneV2"
import {
	computeGardTot,
	computeHSupTot,
	computeRestePayer,
	computeTotalFinal,
	computeTransTot,
	ID_SVP_MECA,
} from "./depFicheUtils"

/**
 * Logique de la fiche dépannage V2 — port du class component DepFiche.js.
 * Le state `dep` reste dans redux (`depInfo.dep`, cases SET_DEP /
 * SET_NOTE / SET_AUTRE inchangées) ; ce hook centralise tous les dispatchs pour
 * qu'aucune mutation n'oublie `setDirty()` (quit guard).
 *
 * `setDirty`/`setClean` viennent de useQuitGuard (container).
 */
export default function useDepFiche({ setDirty, setClean }) {
	const dispatch = useDispatch()
	const depInfo = useSelector((s) => s.depInfo)
	const employes = useSelector((s) => s.masterReducer.employes)
	const fetchEmployesState = useSelector((s) => s.masterReducer.fetchEmployes)
	const {
		moyenPaiements,
		marques,
		fetchMarques: fetchMarquesState,
		modeles,
		fetchModeles: fetchModelesState,
		secteurs,
		fetchSecteurs: fetchSecteursState,
		assistances,
	} = useSelector((s) => s.autreInfo)
	const mdpFacture = useMoyenPaiementFacture()
	const { dep } = depInfo

	// Lieu d'intervention police (GE-7) : enum en dur, plus de référentiel
	// serveur — SECTEURS_POLICE est la source unique (miroir de LIEU_POLICE_*).
	const secteursPolice = Object.keys(SECTEURS_POLICE).map((k) => ({
		id: parseInt(k),
		label: SECTEURS_POLICE[k],
	}))

	// Destination précédente, restaurée quand on décoche « SVP méca ».
	const prevDestRef = useRef({ id: 0, str: "" })
	const prevDepRef = useRef(dep)

	// Lit le dep courant du store (après un dispatch, le `dep` du closure est périmé).
	const getFreshDep = () => dispatch((_, getState) => getState().depInfo.dep)

	const setDepPaid = () =>
		dispatch((d, getState) => {
			const { dep: current } = getState().depInfo
			d({
				type: "SET_DEP_PAID",
				value:
					computeTransTot(current.transactionsv2 || []) ===
					current.totalFinal,
			})
		})

	// ─── Chargement ───

	const fetchOptions = () => {
		dispatch(fetchEmployes())
		dispatch(fetchDepVehicules())
		dispatch(fetchDests())
		dispatch(fetchMoyenPaiements())
		dispatch(fetchMarques())
		dispatch(fetchSecteurs())
		dispatch(fetchAutreEntites()) // assistances (+ assurances) ET garages (sélecteur POI)
		dispatch(fetchPointsInterets()) // parcs / fourrières (sélecteur lieu/destination)
	}

	// Sélecteur de point d'intérêt (lieu/destination) — contrat identique à
	// DepMissionV2 : updatePointInteret(key, value, full). full=true remplace tout l'objet.
	const setPointInteret =
		(field) =>
		(key, value, full = false) => {
			const next = JSON.parse(JSON.stringify(getFreshDep()))
			next[field] = full ? value : { ...next[field], [key]: value }
			setDirty()
			dispatch({ type: "SET_DEP", value: next })
		}
	const setLieuIntervention = setPointInteret("lieu_intervention")
	const setDestinationFinale = setPointInteret("destination_finale")

	// Sélection d'une assistance : vide l'assurance (les labels dépendent de
	// l'assistance choisie) et pose son label pour l'affichage/PDF.
	const setAssistance = (id) => {
		const next = JSON.parse(JSON.stringify(getFreshDep()))
		next.assistance_entite = id
		const found = (assistances || []).find((a) => a.id === id)
		next.assistance_entite_label = found ? found.label : ""
		next.assurance = ""
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	const loadDep = (id) => {
		if (!Number(id)) return Promise.resolve(null)
		return dispatch(fetchDepannage(parseInt(id))).then(() => {
			// Le thunk résout AUSSI en erreur (catchApiError) et
			// FETCH_DEPANNAGE_ERROR laisse `dep` intact : sans ce test, un
			// chargement raté déclarait propre la fiche PRÉCÉDENTE, encore
			// modifiée dans le store.
			const { error } = dispatch(
				(_, getState) => getState().depInfo.fetchDep,
			)
			if (error) {
				// Fiche périmée (supprimée, id inexistant) : on VIDE le slice.
				// Sinon `dep` reste la fiche précédente et le bouton nav
				// « Fiche » continue de pointer sur une fiche impossible à
				// charger. Le dirty de cette fiche précédente part avec elle —
				// on n'arrive ici qu'après avoir accepté de la quitter.
				return dispatch(fetchDepannageReset()).then(() => {
					setClean()
					return null
				})
			}
			const fresh = getFreshDep()
			prevDestRef.current = { id: fresh.destId, str: fresh.dest }
			setClean()
			return fresh
		})
	}

	// ─── Updates (chaque mutation passe ici → setDirty systématique) ───

	const updateDep = (field, e, type = "input") => {
		const next = JSON.parse(JSON.stringify(getFreshDep()))
		let value
		switch (type) {
			case "input":
				if (["marque", "modele"].includes(field)) value = e
				else if (["plaques", "num_dossier"].includes(field))
					value = e.target.value.toUpperCase()
				else value = e.target.value
				break
			case "select":
				// Autofill de la 1re transaction supprimé (référentiel Appel legacy).
				value = parseInt(e.target.value)
				break
			case "checkbox":
				value = !next[field]
				break
			default:
				break
		}
		next[field] = value
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	// Écriture directe d'un champ (DatePicker des dates carte grise / plaques).
	const updateDepField = (field, value) => {
		const next = Object.assign({}, getFreshDep())
		next[field] = value
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	// Blur d'un champ numérique vide → 0 (comportement legacy onFocusLost).
	const onFocusLost = (field, e) => {
		if (e.target.value === "") updateDepField(field, 0)
	}

	// Checkboxes à effet de bord : SVP méca écrase/restaure la destination,
	// Fourrière pré-remplit la 1re transaction sur fiche neuve (valeurs métier
	// codées en dur, identiques au legacy). Gère aussi « surPlace » (simple toggle).
	const specialDest = (checkbox) => {
		const next = JSON.parse(JSON.stringify(getFreshDep()))
		switch (checkbox) {
			case "svpMeca":
				if (!next.svpMeca) {
					const svp = depInfo.dests.find(
						(dest) => dest.id === ID_SVP_MECA,
					)
					next.destId = ID_SVP_MECA
					next.dest = svp ? svp.nom : next.dest
				} else {
					next.destId = prevDestRef.current.id
					next.dest = prevDestRef.current.str
				}
				break
			case "four":
				// Fiche neuve : la coche pré-remplit une TransactionV2
				// « Fourrière » (Facture, dépanneur caissier, total présumé) ;
				// décochée, la ligne pré-remplie non sauvée est retirée.
				if (next.id === 0) {
					if (!next.four) {
						next.transactionsv2 = [
							...(next.transactionsv2 || []),
							{
								id: Math.random(),
								date: "",
								payeParSelect: 1,
								quittance: null,
								facture: null,
								moyenPaie: mdpFacture ? mdpFacture.id : 0,
								caissier: next.depanneur,
								total: next.totalFinal,
								details: "1217 Meyrin",
								raison: "Fourrière",
								depannage: 0,
								dep_mission: null,
							},
						]
						next.livInfo = "Fourrière"
					} else {
						next.transactionsv2 = (
							next.transactionsv2 || []
						).filter(
							(t) =>
								!(
									t.raison === "Fourrière" &&
									!Number.isInteger(t.id)
								),
						)
						if (next.livInfo === "Fourrière") next.livInfo = ""
					}
				}
				break
			default:
				break
		}
		next[checkbox] = !next[checkbox]
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	// ─── Transactions / notes / autres ───

	// ─── Transactions V2 (chantier DepTransaction → TransactionV2) ───
	// La fiche saisit ses transactions dans le modèle général (composant
	// partagé others/TransactionV2, pattern AtelierV2) — DepTransaction est
	// mort. Les encaissements terrain (dep_mission posé, GE-10) sont dans la
	// même liste mais verrouillés côté UI — le POST fiche ne les touche jamais.
	const setTransactionsV2 = (transactionsv2, dirty = true) => {
		const next = Object.assign({}, getFreshDep())
		next.transactionsv2 = transactionsv2
		if (dirty) setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	const updateTransactionV2 = (index, field, value) => {
		const v = ["caissier", "moyenPaie", "payeParSelect"].includes(field)
			? parseInt(value)
			: value
		setTransactionsV2(
			(getFreshDep().transactionsv2 || []).map((t, i) => {
				if (i !== index) return t
				const updated = { ...t, [field]: v }
				if (field === "payeParSelect" && v === PAYE_PAR_MASTER)
					return appliquerPerte(updated)
				return updated
			}),
		)
	}

	const removeTransactionV2 = (index) => {
		const next = Object.assign({}, getFreshDep())
		const tx = (next.transactionsv2 || [])[index]
		// GE-60 (revue I1) : un encaissement de couverture n'est supprimé côté
		// serveur que sur demande explicite (pas par son absence du payload) ;
		// Jordan 19.10 : idem pour une ligne protégée (datée, WinBIZ,
		// rapprochée…) que le boss supprime.
		if (tx && Number.isInteger(tx.id))
			next.transactions_supprimees = [
				...(next.transactions_supprimees || []),
				tx.id,
			]
		next.transactionsv2 = (next.transactionsv2 || []).filter(
			(_, i) => i !== index,
		)
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	const addTransactionV2 = (raison = "") =>
		setTransactionsV2([
			...(getFreshDep().transactionsv2 || []),
			{
				id: Math.random(),
				date: "",
				payeParSelect: -1,
				quittance: null,
				facture: null,
				details: "",
				moyenPaie: 0,
				caissier: 0,
				total: 0,
				raison,
				depannage: getFreshDep().id,
				dep_mission: null,
			},
		])

	// Re-pose la liste depuis le serveur (après génération / suppression d'un
	// document) — sans marquer la fiche dirty : rien à sauvegarder. FUSION et
	// non remplacement (règles dans utilities/transactionsV2) : la liste locale
	// contient de la saisie jamais POSTée que le serveur ignore.
	const refreshTransactionsV2 = (transactionsv2) =>
		setTransactionsV2(
			mergeTransactionsV2(
				getFreshDep().transactionsv2 || [],
				transactionsv2,
			),
			false,
		)

	const updateNote = (field, e, index) => {
		const note = Object.assign({}, getFreshDep().notes[index])
		if (field === "print") note[field] = !note[field]
		else note[field] = e.target.value
		setDirty()
		dispatch({ type: "SET_NOTE", value: note })
	}

	const removeNote = (note) => {
		setDirty()
		dispatch({ type: "REMOVE_NOTE", value: note.id })
	}

	// `print` = true → commentaire client (imprimé sur le PDF) ; false → note interne.
	const addNote = (print = false) => {
		setDirty()
		dispatch({
			type: "SET_NOTE",
			value: { id: Math.random(1000) * 1000, text: "", print },
		})
	}

	const updateAutre = (field, e, index) => {
		const autre = Object.assign({}, getFreshDep().autres[index])
		autre[field] = e.target.value
		setDirty()
		dispatch({ type: "SET_AUTRE", value: autre })
	}

	const removeAutre = (autre) => {
		setDirty()
		dispatch({ type: "REMOVE_AUTRE", value: autre.id })
	}

	const addAutre = () => {
		setDirty()
		dispatch({
			type: "SET_AUTRE",
			value: { id: Math.random(1000) * 1000, total: 0, info: "" },
		})
	}

	// ─── Gardiennage (calcul auto des jours parc) ───

	// Gardiennage : calcul commun avec la modale « Livrer / Restituer »
	// (livrerRestituer.joursGardiennage — règles GE-14, jusqu'à la
	// restitution saisie, sinon maintenant).
	const gardButton = () => {
		const current = getFreshDep()
		const jours = joursGardiennage(current)
		const next = Object.assign({}, current)
		next.gardQ = jours
		// gardT : saisie libre (grille plus tard) — 40 posé seulement si vide.
		if (!parseFloat(next.gardT)) next.gardT = jours > 0 ? 40 : 0
		next.gardTot = computeGardTot(next)
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
	}

	// ─── Restitution (modale « Livrer / Restituer », Jordan 30.10) ───
	// Applique la restitution au formulaire (livrerRestituer) → la ligne
	// « Restitution » créée (« Payé maintenant »), sinon null. Rien n'est
	// sauvé : la fiche devient modifiée, la quittance suit le save.
	const restituer = (choix) => {
		const { dep: next, transaction } = appliquerRestitution(
			getFreshDep(),
			choix,
		)
		setDirty()
		dispatch({ type: "SET_DEP", value: next })
		return transaction
	}

	// F4 (boss) : date du jour sur l'unique transaction.
	const bossTransactionDate = () => {
		// Date du jour sur LA transaction fiche (les encaissements terrain
		// verrouillés ne comptent pas) — format ISO du composant partagé.
		const fresh = getFreshDep()
		const txsFiche = (fresh.transactionsv2 || []).filter(
			(t) => !t.dep_mission,
		)
		if (txsFiche.length !== 1) return
		const next = Object.assign({}, fresh)
		// Jordan 15.10 : dater seulement — une date posée ne se réécrit pas
		// ici (correction : boss / admin, champ Date).
		next.transactionsv2 = next.transactionsv2.map((t) =>
			t.dep_mission || t.date
				? t
				: { ...t, date: moment().format("YYYY-MM-DD") },
		)
		setDirty()
		dispatch(setDep(next))
	}

	// ─── Recalcul des totaux (port littéral de componentDidUpdate 548-651) ───
	// UN seul effet, diff champ par champ via prevDepRef : c'est le guard
	// `shouldUpdate` qui évite la boucle SET_DEP → effet → SET_DEP.

	useEffect(() => {
		if (depInfo.fetchDep.loading) {
			prevDepRef.current = dep
			return
		}
		const prevDep = prevDepRef.current
		prevDepRef.current = dep
		if (prevDep !== dep) {
			let shouldUpdate = false
			let shouldCallback = false
			const nextDep = Object.assign({}, dep)
			if (prevDep.hSupQ !== dep.hSupQ || prevDep.hSupT !== dep.hSupT) {
				nextDep.hSupTot = computeHSupTot(dep)
				shouldUpdate = true
			}
			if (prevDep.gardQ !== dep.gardQ || prevDep.gardT !== dep.gardT) {
				nextDep.gardTot = computeGardTot(dep)
				shouldUpdate = true
			}
			if (
				prevDep.baseTot !== dep.baseTot ||
				prevDep.hSupTot !== dep.hSupTot ||
				prevDep.gardTot !== dep.gardTot ||
				prevDep.restiTot !== dep.restiTot ||
				prevDep.livraisons !== dep.livraisons ||
				JSON.stringify(prevDep.autres) !== JSON.stringify(dep.autres)
			) {
				nextDep.totalFinal = computeTotalFinal(dep)
				shouldUpdate = true
				shouldCallback = true
			}
			if (shouldUpdate) {
				if (!shouldCallback) {
					dispatch({ type: "SET_DEP", value: nextDep })
				} else {
					dispatch(setDep(nextDep)).then(setDepPaid)
				}
			}
			if (
				prevDep.transactionsv2 !== dep.transactionsv2 ||
				prevDep.totalFinal !== dep.totalFinal
			) {
				setDepPaid()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dep, depInfo.fetchDep.loading])

	// Mount : options + « paid » ; unmount : le badge repasse à payé (legacy).
	useEffect(() => {
		fetchOptions()
		setDepPaid()
		return () => {
			dispatch({ type: "SET_DEP_PAID", value: true })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const restePayer = useMemo(
		() => computeRestePayer(dep),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dep.transactions, dep.transactionsv2, dep.totalFinal],
	)

	return {
		depInfo,
		dep,
		employes,
		fetchEmployesState,
		moyenPaiements,
		mdpFacture,
		marques,
		fetchMarquesState,
		modeles,
		fetchModelesState,
		secteurs,
		fetchSecteursState,
		assistances,
		secteursPolice,
		restePayer,
		prevDestRef,
		loadDep,
		setDepPaid,
		updateDep,
		updateDepField,
		onFocusLost,
		specialDest,
		setAssistance,
		setLieuIntervention,
		setDestinationFinale,
		updateTransactionV2,
		removeTransactionV2,
		addTransactionV2,
		refreshTransactionsV2,
		updateNote,
		removeNote,
		addNote,
		updateAutre,
		removeAutre,
		addAutre,
		gardButton,
		restituer,
		bossTransactionDate,
	}
}
